export type SectorId = "senior-living" | "information-technology" | "healthcare" | "general";

export type SectorIconName =
  | "heart-handshake"
  | "monitor-smartphone"
  | "stethoscope"
  | "building-2";

export interface PackDepartment {
  id: string;
  name: string;
  description: string;
  roles: string[];
}

export interface PackAssetType {
  id: string;
  name: string;
  attributes: string[];
}

export interface SectorPack {
  id: SectorId;
  name: string;
  tagline: string;
  iconName: SectorIconName;
  departments: PackDepartment[];
  assetTypes: PackAssetType[];
}

/**
 * Sector pack manifests — shared seed data consumed by both the server
 * seeder and the client wizard. Copied into the per-instance working state
 * when a sector is selected (see WorkforcePackContext).
 * Shaped as pack seed data so the future server-backed pack engine
 * (docs/vertical-packs.md) can consume these manifests directly.
 */
export const SECTOR_PACKS: readonly SectorPack[] = [
  {
    id: "senior-living",
    name: "Senior Living",
    tagline: "Communities across IL / AL / MC — care, dining, and facility operations.",
    iconName: "heart-handshake",
    departments: [
      {
        id: "care-nursing",
        name: "Care & Nursing",
        description: "Resident care, medication management, and clinical oversight.",
        roles: ["Caregiver", "Med Tech", "Nurse (RN/LPN)", "Director of Nursing"],
      },
      {
        id: "dining",
        name: "Dining Services",
        description: "Kitchen, meal service, and dietary programs.",
        roles: ["Cook", "Dining Server", "Dining Services Director"],
      },
      {
        id: "housekeeping",
        name: "Housekeeping & Laundry",
        description: "Community cleanliness and laundry services.",
        roles: ["Housekeeper", "Laundry Aide"],
      },
      {
        id: "activities",
        name: "Life Enrichment",
        description: "Resident activities, events, and engagement programs.",
        roles: ["Activities Assistant", "Life Enrichment Director"],
      },
      {
        id: "maintenance",
        name: "Maintenance",
        description: "Building upkeep, safety checks, and work orders.",
        roles: ["Maintenance Technician", "Maintenance Director"],
      },
      {
        id: "administration",
        name: "Administration",
        description: "Community leadership and business office.",
        roles: ["Executive Director", "Business Office Manager", "Receptionist"],
      },
      {
        id: "sales-marketing",
        name: "Sales & Marketing",
        description: "Tours, move-ins, and community outreach.",
        roles: ["Sales Director", "Move-in Coordinator"],
      },
    ],
    assetTypes: [
      {
        id: "resident-bed",
        name: "Resident Beds",
        attributes: ["Room", "Bed size", "Mattress type", "Condition"],
      },
      {
        id: "med-cart",
        name: "Med Carts",
        attributes: ["Location", "Lock type", "Last audit date"],
      },
      {
        id: "access-badge",
        name: "Access Badges",
        attributes: ["Badge ID", "Access level", "Issued to"],
      },
      {
        id: "nurse-call-device",
        name: "Nurse-Call Devices",
        attributes: ["Room", "Device model", "Battery status"],
      },
      {
        id: "community-vehicle",
        name: "Community Vehicles",
        attributes: ["Make & model", "VIN", "Seats", "Wheelchair lift"],
      },
    ],
  },
  {
    id: "information-technology",
    name: "Information Technology",
    tagline: "Software and technology teams — engineering, support, and corporate.",
    iconName: "monitor-smartphone",
    departments: [
      {
        id: "engineering",
        name: "Engineering",
        description: "Product development and quality.",
        roles: ["Software Engineer", "Senior Engineer", "QA Engineer", "Engineering Manager"],
      },
      {
        id: "it-support",
        name: "IT Support",
        description: "Internal IT, devices, and access administration.",
        roles: ["Support Technician", "IT Administrator"],
      },
      {
        id: "product-design",
        name: "Product & Design",
        description: "Product management and user experience.",
        roles: ["Product Manager", "Product Designer"],
      },
      {
        id: "sales-marketing",
        name: "Sales & Marketing",
        description: "Revenue and demand generation.",
        roles: ["Account Executive", "Marketing Manager"],
      },
      {
        id: "hr-operations",
        name: "HR & Operations",
        description: "People operations and workplace.",
        roles: ["HR Generalist", "Office Manager"],
      },
      {
        id: "finance",
        name: "Finance",
        description: "Accounting and financial planning.",
        roles: ["Accountant", "Controller"],
      },
    ],
    assetTypes: [
      {
        id: "laptop",
        name: "Laptops",
        attributes: ["Serial number", "Model", "OS", "Assigned to", "Warranty end"],
      },
      {
        id: "monitor",
        name: "Monitors",
        attributes: ["Serial number", "Size", "Assigned to"],
      },
      {
        id: "software-license",
        name: "Software Licenses",
        attributes: ["Vendor", "Seats", "Renewal date", "Annual cost"],
      },
      {
        id: "server",
        name: "Servers",
        attributes: ["Hostname", "Location", "Specs", "Assigned to"],
      },
      {
        id: "access-token",
        name: "Access Tokens & Keys",
        attributes: ["Type", "Issued to", "Expiry"],
      },
    ],
  },
  {
    id: "healthcare",
    name: "Healthcare (Provider Group)",
    tagline: "Clinics and provider groups — clinical, front office, and billing.",
    iconName: "stethoscope",
    departments: [
      {
        id: "clinical",
        name: "Clinical",
        description: "Providers and clinical support staff.",
        roles: ["Physician", "Nurse Practitioner", "Registered Nurse", "Medical Assistant"],
      },
      {
        id: "front-office",
        name: "Front Office",
        description: "Reception, scheduling, and patient intake.",
        roles: ["Front Desk Coordinator", "Scheduler"],
      },
      {
        id: "billing",
        name: "Billing & Coding",
        description: "Claims, billing, and medical coding.",
        roles: ["Medical Biller", "Medical Coder"],
      },
      {
        id: "compliance",
        name: "Compliance",
        description: "Regulatory compliance and privacy.",
        roles: ["Compliance Officer", "Privacy Officer"],
      },
      {
        id: "administration",
        name: "Administration",
        description: "Practice leadership and operations.",
        roles: ["Practice Manager", "Office Administrator"],
      },
    ],
    assetTypes: [
      {
        id: "exam-equipment",
        name: "Exam Equipment",
        attributes: ["Type", "Room", "Last calibration"],
      },
      {
        id: "workstation-on-wheels",
        name: "Workstations on Wheels",
        attributes: ["Asset tag", "Battery health", "Department"],
      },
      {
        id: "badge",
        name: "Badges",
        attributes: ["Badge ID", "Access level"],
      },
      {
        id: "tablet",
        name: "Tablets",
        attributes: ["Serial number", "Model", "Assigned to"],
      },
    ],
  },
  {
    id: "general",
    name: "General",
    tagline: "A minimal starting structure for any other organization.",
    iconName: "building-2",
    departments: [
      {
        id: "operations",
        name: "Operations",
        description: "Day-to-day business operations.",
        roles: ["Operations Manager", "Coordinator"],
      },
      {
        id: "hr",
        name: "Human Resources",
        description: "People and hiring.",
        roles: ["HR Manager", "Recruiter"],
      },
      {
        id: "finance",
        name: "Finance",
        description: "Accounting and payroll.",
        roles: ["Accountant"],
      },
      {
        id: "it",
        name: "IT",
        description: "Systems and support.",
        roles: ["IT Support"],
      },
    ],
    assetTypes: [
      {
        id: "equipment",
        name: "Equipment",
        attributes: ["Serial number", "Category", "Condition"],
      },
      {
        id: "license",
        name: "Licenses",
        attributes: ["Vendor", "Seats", "Renewal date"],
      },
    ],
  },
];

export function getSectorPack(id: SectorId): SectorPack {
  const pack = SECTOR_PACKS.find((p) => p.id === id);
  if (!pack) {
    throw new Error(`Unknown sector pack: ${id}`);
  }
  return pack;
}
